import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import { useUser } from "../../context/user";
import { recoverPassword } from "../../supabase";
import "./AuthForm.css";

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

const EASE = [0.4, 0, 0.2, 1];
const LAYOUT_TRANSITION = { duration: 0.32, ease: EASE }; // mirrors --booth-motion-line
const FIELD_TRANSITION = { duration: 0.2, ease: EASE }; // mirrors --booth-motion-emphasis
const FADE_TRANSITION = { duration: 0.17, ease: EASE }; // mirrors --booth-motion-fade

/** Log in / register card for the auth page. onNotify(message, severity) reports status up. */
function AuthForm({ onNotify }) {
  const navigate = useNavigate();
  const user = useUser();
  const [mode, setMode] = useState("login");
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState(false);
  const [pw, setPw] = useState("");
  const [pwErr, setPwErr] = useState(false);
  const [name, setName] = useState("");
  const [nameErr, setNameErr] = useState(false);
  const [resetPw, setResetPw] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailErr, setResetEmailErr] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validEmail = EMAIL_RE.test(email);
    const validPw = pw.length >= 8;
    const validName = mode === "login" || name.length > 0;
    setEmailErr(!validEmail);
    setPwErr(!validPw);
    setNameErr(mode === "register" && !validName);
    if (!validEmail || !validPw || !validName) return;

    if (mode === "login") {
      try {
        const verifiedResponse = await user.login(email, pw, rememberMe);
        if (verifiedResponse.verified) {
          navigate("/dashboard");
        } else {
          onNotify("Login failed. Verify your email before logging in.", "error");
        }
      } catch (error) {
        onNotify("Login failed. Please try again.", "error");
      }
    } else {
      const result = await user.register(email, pw, name);
      onNotify(result.message, result.success ? "success" : "error");
      if (result.success) {
        setEmail("");
        setPw("");
        setName("");
        setMode("login");
      }
    }
  };

  const handlePasswordReset = async (event) => {
    event.preventDefault();
    if (!EMAIL_RE.test(resetEmail)) {
      setResetEmailErr(true);
      return;
    }
    setResetEmailErr(false);
    try {
      await recoverPassword(resetEmail);
      onNotify("Password reset email sent! Check your inbox for instructions.", "success");
      setResetPw(false);
      setResetEmail("");
    } catch (error) {
      onNotify(error.message || "Failed to send password reset email. Please try again.", "error");
    }
  };

  if (user.current) {
    return (
      <div className="booth-auth-card booth-auth-card--recovered">
        <svg className="booth-auth-check" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
          <circle cx="20" cy="20" r="18.5" stroke="currentColor" strokeWidth="1.5" />
          <path d="M12 20.5L17 25.5L28 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <h1 className="booth-auth-title">Session recovered</h1>
        <button type="button" className="booth-btn booth-btn--primary booth-auth-submit" onClick={() => navigate("/dashboard")}>
          Go to dashboard <span aria-hidden="true">→</span>
        </button>
      </div>
    );
  }

  if (resetPw) {
    return (
      <div className="booth-auth-card">
        <p className="booth-auth-eyebrow">Step inside</p>
        <h1 className="booth-auth-title">Reset your password</h1>
        <form onSubmit={handlePasswordReset} className="booth-auth-form" noValidate>
          <label className="booth-field">
            <span className="booth-field-label">Email</span>
            <input
              type="email"
              className={`booth-field-input ${resetEmailErr ? "booth-field-input--error" : ""}`}
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
            />
            {resetEmailErr && <span className="booth-field-error">Enter a valid email</span>}
          </label>
          <button type="submit" className="booth-btn booth-btn--primary booth-auth-submit">
            Send reset email
          </button>
          <button type="button" className="booth-link-underline booth-auth-back" onClick={() => setResetPw(false)}>
            Back to log in
          </button>
        </form>
      </div>
    );
  }

  return (
    <MotionConfig transition={LAYOUT_TRANSITION} reducedMotion="user">
      <motion.div layout className="booth-auth-card">
        <motion.p layout="position" className="booth-auth-eyebrow">
          Step inside
        </motion.p>
        <motion.h1
          key={mode}
          layout="position"
          className="booth-auth-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={FADE_TRANSITION}
        >
          {mode === "login" ? "Sign in" : "Register"}
        </motion.h1>
        <motion.div layout="position" className="booth-auth-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "login"}
            className={`booth-auth-tab ${mode === "login" ? "booth-auth-tab--active" : ""}`}
            onClick={() => setMode("login")}
          >
            Log in
            {mode === "login" && <motion.span layoutId="authTabUnderline" className="booth-auth-tab-underline" />}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            className={`booth-auth-tab ${mode === "register" ? "booth-auth-tab--active" : ""}`}
            onClick={() => setMode("register")}
          >
            Register
            {mode === "register" && <motion.span layoutId="authTabUnderline" className="booth-auth-tab-underline" />}
          </button>
        </motion.div>
        <motion.form layout onSubmit={handleSubmit} className="booth-auth-form" noValidate>
          <AnimatePresence mode="popLayout" initial={false}>
            {mode === "register" && (
              <motion.label
                key="name-field"
                layout
                className="booth-field"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={FIELD_TRANSITION}
              >
                <span className="booth-field-label">Name</span>
                <input
                  type="text"
                  autoComplete="name"
                  className={`booth-field-input ${nameErr ? "booth-field-input--error" : ""}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {nameErr && <span className="booth-field-error">Name is required</span>}
              </motion.label>
            )}
          </AnimatePresence>
          <motion.label layout="position" className="booth-field">
            <span className="booth-field-label">Email</span>
            <input
              type="email"
              autoComplete="email"
              className={`booth-field-input ${emailErr ? "booth-field-input--error" : ""}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {emailErr && <span className="booth-field-error">Enter a valid email</span>}
          </motion.label>
          <motion.label layout="position" className="booth-field">
            <span className="booth-field-label">Password</span>
            <input
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              className={`booth-field-input ${pwErr ? "booth-field-input--error" : ""}`}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
            />
            {pwErr && <span className="booth-field-error">Password must be at least 8 characters</span>}
          </motion.label>
          {mode === "login" && (
            <motion.div layout="position" className="booth-auth-row">
              <label className="booth-checkbox">
                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                Remember me
              </label>
              <button type="button" className="booth-link-underline booth-auth-forgot" onClick={() => setResetPw(true)}>
                Forgot password?
              </button>
            </motion.div>
          )}
          <motion.button layout="position" type="submit" className="booth-btn booth-btn--primary booth-auth-submit">
            <motion.span key={mode} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={FADE_TRANSITION}>
              {mode === "login" ? "Log in" : "Create account"}
            </motion.span>{" "}
            <span aria-hidden="true">→</span>
          </motion.button>
        </motion.form>
      </motion.div>
    </MotionConfig>
  );
}

export default AuthForm;
